import { Button, TextField, useToast } from '@kanos/ui';
import { formatDateTime, meQuery, ProblemAlert, useIdempotencyKey } from '@kanos/web-core';
import { useQuery } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useRevoke, type OfferedContract, type RevocationReceipt } from './engagement-queries';

/** A part of the contract's page, as a card of glass. */
export function ContractSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="glass grid gap-4 rounded-lg p-5 sm:p-6">
      <h2 className="text-xl font-semibold tracking-[-0.02em]">{title}</h2>
      {children}
    </section>
  );
}

/**
 * The withdrawal function (§ 356a BGB): „Vertrag widerrufen“ in sight while the period runs,
 * then name, contract and address confirmed, then „Widerruf bestätigen“ - and the receipt at once.
 */
export function Receipt({ receipt, firmName }: { receipt: RevocationReceipt; firmName: string }) {
  return (
    <ContractSection title="Eingangsbestätigung Ihres Widerrufs">
      <p className="text-sm" role="status">
        Ihr Widerruf ist am {formatDateTime(receipt.receivedAt)} bei {firmName} eingegangen. Inhalt:
        Widerruf des {receipt.contract}; Name: {receipt.name}; Bestätigung an: {receipt.email}.
        Diese Bestätigung erhalten Sie auch per E-Mail.
      </p>
    </ContractSection>
  );
}

export function Withdrawal({
  caseId,
  offer,
  onDone,
}: {
  caseId: string;
  offer: OfferedContract;
  onDone: (receipt: RevocationReceipt) => void;
}) {
  const me = useQuery(meQuery);
  const revoke = useRevoke(caseId);
  const toast = useToast();
  const [idempotencyKey] = useIdempotencyKey();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(offer.clientName);
  const [email, setEmail] = useState(me.data?.email ?? '');
  return (
    <ContractSection title="Widerruf">
      {!open ? (
        <Button variant="secondary" className="justify-self-start" onPress={() => setOpen(true)}>
          <Undo2 aria-hidden className="size-4" />
          Vertrag widerrufen
        </Button>
      ) : (
        <div className="grid gap-4">
          <p className="text-sm">
            Sie widerrufen den Steuerberatungsvertrag, Fassung {String(offer.version)}, mit{' '}
            {offer.firmName}. Bitte bestätigen Sie Ihren Namen und die E-Mail-Adresse für die
            Eingangsbestätigung.
          </p>
          <TextField
            label="Ihr Name"
            placeholder="z. B. Erika Mustermann"
            value={name}
            onChange={setName}
            isRequired
          />
          <TextField
            label="E-Mail-Adresse für die Bestätigung"
            placeholder="z. B. erika.mustermann@example.org"
            type="email"
            value={email}
            onChange={setEmail}
            isRequired
          />
          {revoke.isError && <ProblemAlert error={revoke.error} />}
          <div className="flex flex-wrap gap-2">
            <Button variant="quiet" onPress={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              isPending={revoke.isPending}
              onPress={() =>
                revoke.mutate(
                  { body: { version: offer.version, name, email }, idempotencyKey },
                  {
                    onSuccess: (done) => {
                      onDone(done);
                      toast({ tone: 'success', title: 'Widerruf eingegangen' });
                    },
                  },
                )
              }
            >
              Widerruf bestätigen
            </Button>
          </div>
        </div>
      )}
    </ContractSection>
  );
}
